import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import StatCard from '../components/ui/StatCard';
import { TrendingUp, Layers, Zap, DollarSign } from 'lucide-react';

export default function EarningsPage() {
  const { data: investments } = useQuery({ queryKey: ['investments'], queryFn: () => api.get('/investments').then(r => r.data) });
  const { data: levelIncomeData } = useQuery({ queryKey: ['levelIncome'], queryFn: () => api.get('/investments/level-income/me').then(r => r.data) });
  const { data: commissions } = useQuery({ queryKey: ['myCommissions'], queryFn: () => api.get('/commissions/me').then(r => r.data) });
  const { data: bonuses } = useQuery({ queryKey: ['myBonuses'], queryFn: () => api.get('/bonuses/me').then(r => r.data) });

  const investmentsList = investments || [];
  const levelIncomeList = levelIncomeData || [];
  const commissionData = commissions?.data || [];
  const bonusData = bonuses?.data || [];

  const totalRoi = investmentsList.reduce((s: number, i: any) => s + parseFloat(i.totalEarned || '0'), 0);
  const totalDirect = commissionData.filter((c: any) => c.rule?.type === 'DIRECT').reduce((s: number, c: any) => s + parseFloat(c.amount), 0);
  const totalLevel = levelIncomeList.reduce((s: number, l: any) => s + parseFloat(l.paidAmount || '0'), 0);
  const totalBonus = bonusData.reduce((s: number, b: any) => s + parseFloat(b.amount), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">Earnings</h1>
        <p className="text-slate-400 mt-1">Track all your income streams.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Daily ROI Income" value={formatCurrency(totalRoi)} icon={TrendingUp} />
        <StatCard title="Direct Income" value={formatCurrency(totalDirect)} icon={DollarSign} />
        <StatCard title="Level Income" value={formatCurrency(totalLevel)} icon={Layers} />
        <StatCard title="Bonus Income" value={formatCurrency(totalBonus)} icon={Zap} />
      </div>

      <Card>
        <CardHeader><h2 className="text-base font-semibold text-white">Level Income Details</h2></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="d-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Plan</th>
                  <th>Pool</th>
                  <th>Daily</th>
                  <th>Progress</th>
                  <th>Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {levelIncomeList.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500">No level income allocations yet</td></tr>
                ) : (
                  levelIncomeList.map((l: any) => (
                    <tr key={l.id}>
                      <td className="font-medium text-white">Level {l.level}</td>
                      <td className="text-slate-400">{l.investment?.plan?.name || '-'}</td>
                      <td className="text-slate-400">{formatCurrency(l.poolAmount)}</td>
                      <td className="text-slate-400">{formatCurrency(l.dailyAmount)}</td>
                      <td className="text-slate-400">{l.completedDays}/{l.workingDaysTotal}</td>
                      <td className="font-medium text-emerald-400">{formatCurrency(l.paidAmount)}</td>
                      <td><Badge status={l.status} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><h2 className="text-base font-semibold text-white">Direct Commission History</h2></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="d-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {commissionData.filter((c: any) => c.rule?.type === 'DIRECT').length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-500">No direct commissions yet</td></tr>
                ) : (
                  commissionData.filter((c: any) => c.rule?.type === 'DIRECT').map((c: any) => (
                    <tr key={c.id}>
                      <td><Badge status="DIRECT" /></td>
                      <td className="font-medium text-emerald-400">{formatCurrency(c.amount)}</td>
                      <td className="text-slate-400">{c.description || '-'}</td>
                      <td className="text-slate-400">{new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
